/**
 * Leitura de JSON igual ao `json.loads` do Python (usado pelo `r.json()` do httpx no painel
 * antigo). Serve para a página de violações mostrar as mesmas mensagens quando a resposta
 * vem quebrada (ex.: "Expecting ':' delimiter: line 1 column 34 (char 33)") e para
 * separar números inteiros de números com casas decimais, como o Python faz.
 *
 * Números com ponto/expoente (ex.: `1.0`, `1e20`), `NaN` e `Infinity` viram `NumeroPython`
 * com `flutuante = true`. Inteiros grandes demais para o JavaScript guardar sem perder
 * dígitos também viram `NumeroPython`, com o texto exato em `texto`.
 */

/** Número do JSON que o JavaScript não representa do mesmo jeito que o Python. */
export class NumeroPython {
  constructor(
    /** Valor aproximado (o que o JavaScript consegue guardar). */
    readonly valor: number,
    /** `true` = `float` no Python; `false` = `int` grande. */
    readonly flutuante: boolean,
    /** Texto exato do número no JSON (para inteiros grandes). */
    readonly texto: string
  ) {}
}

/** Erro de leitura com a mesma mensagem do `json.JSONDecodeError`. */
export class ErroJsonPython extends Error {}

/** Nenhum valor começa nesta posição (o `StopIteration` do scanner do Python). */
class ParadaDeLeitura extends Error {
  constructor(readonly posicao: number) {
    super('Expecting value')
  }
}

/** Erro de leitura com mensagem própria, na posição informada. */
class FalhaDeLeitura extends Error {
  constructor(
    readonly mensagem: string,
    readonly posicao: number
  ) {
    super(mensagem)
  }
}

const ESPACOS = new Set([' ', '\t', '\n', '\r'])
const ehDigito = (c: string | undefined) => c !== undefined && c >= '0' && c <= '9'

const ESCAPES: Record<string, string> = {
  '"': '"',
  '\\': '\\',
  '/': '/',
  b: '\b',
  f: '\f',
  n: '\n',
  r: '\r',
  t: '\t',
}

/** Mensagem no formato do Python: "msg: line L column C (char P)", contando caracteres. */
function mensagemPython(texto: string, mensagem: string, posicao: number): string {
  const antes = texto.slice(0, posicao)
  // O Python conta caracteres (pontos de código); o JavaScript conta unidades UTF-16.
  const caractere = Array.from(antes).length
  const linha = antes.split('\n').length
  const inicioLinha = antes.lastIndexOf('\n') + 1
  const coluna = Array.from(antes.slice(inicioLinha)).length + 1
  return `${mensagem}: line ${linha} column ${coluna} (char ${caractere})`
}

function lerHex(texto: string, inicio: number, falhaEm: number): number {
  let valor = 0
  for (let i = inicio; i < inicio + 4; i++) {
    const c = texto[i] ?? ''
    if (!/^[0-9a-fA-F]$/.test(c)) throw new FalhaDeLeitura('Invalid \\uXXXX escape', falhaEm)
    valor = valor * 16 + parseInt(c, 16)
  }
  return valor
}

/** Lê a string que começa depois das aspas em `inicio` (como o `scanstring` do Python). */
function lerTexto(texto: string, inicio: number): [string, number] {
  const aspas = inicio - 1
  const tamanho = texto.length
  let saida = ''
  let fim = inicio
  for (;;) {
    let proximo = fim
    let c = ''
    for (; proximo < tamanho; proximo++) {
      c = texto[proximo] ?? ''
      if (c === '"' || c === '\\') break
      if (c.charCodeAt(0) <= 0x1f) {
        throw new FalhaDeLeitura('Invalid control character at', proximo)
      }
    }
    if (proximo >= tamanho || !(c === '"' || c === '\\')) {
      throw new FalhaDeLeitura('Unterminated string starting at', aspas)
    }
    saida += texto.slice(fim, proximo)
    proximo += 1
    if (c === '"') return [saida, proximo]
    if (proximo === tamanho) throw new FalhaDeLeitura('Unterminated string starting at', aspas)
    const e = texto[proximo] ?? ''
    if (e !== 'u') {
      fim = proximo + 1
      const trocado = ESCAPES[e]
      if (trocado === undefined) throw new FalhaDeLeitura('Invalid \\escape', fim - 2)
      saida += trocado
      continue
    }
    proximo += 1
    fim = proximo + 4
    if (fim >= tamanho) throw new FalhaDeLeitura('Invalid \\uXXXX escape', proximo - 1)
    let codigo = lerHex(texto, proximo, fim - 5)
    proximo = fim
    // Par de substitutos (😀) vira um único caractere.
    if (
      codigo >= 0xd800 &&
      codigo <= 0xdbff &&
      fim + 6 < tamanho &&
      texto[proximo] === '\\' &&
      texto[proximo + 1] === 'u'
    ) {
      const fim2 = fim + 6
      const baixo = lerHex(texto, proximo + 2, fim2 - 5)
      if (baixo >= 0xdc00 && baixo <= 0xdfff) {
        codigo = 0x10000 + ((codigo - 0xd800) << 10) + (baixo - 0xdc00)
        fim = fim2
      }
    }
    saida += String.fromCodePoint(codigo)
  }
}

/** Lê um número (como o `_match_number` do Python). */
function lerNumero(texto: string, inicio: number): [unknown, number] {
  const ultimo = texto.length - 1
  let i = inicio
  if (texto[i] === '-') {
    i += 1
    if (i > ultimo) throw new ParadaDeLeitura(inicio)
  }
  const c = texto[i] ?? ''
  if (c >= '1' && c <= '9') {
    i += 1
    while (i <= ultimo && ehDigito(texto[i])) i += 1
  } else if (c === '0') i += 1
  else throw new ParadaDeLeitura(inicio)
  let flutuante = false
  if (i < ultimo && texto[i] === '.' && ehDigito(texto[i + 1])) {
    flutuante = true
    i += 2
    while (i <= ultimo && ehDigito(texto[i])) i += 1
  }
  if (i < ultimo && (texto[i] === 'e' || texto[i] === 'E')) {
    const inicioExpoente = i
    i += 1
    if (i < ultimo && (texto[i] === '-' || texto[i] === '+')) i += 1
    while (i <= ultimo && ehDigito(texto[i])) i += 1
    if (ehDigito(texto[i - 1])) flutuante = true
    else i = inicioExpoente
  }
  const numero = texto.slice(inicio, i)
  const valor = Number(numero)
  if (flutuante) return [new NumeroPython(valor, true, numero), i]
  if (Number.isSafeInteger(valor)) return [valor === 0 ? 0 : valor, i]
  return [new NumeroPython(valor, false, BigInt(numero).toString()), i]
}

function pularEspacos(texto: string, i: number): number {
  while (i < texto.length && ESPACOS.has(texto[i] ?? '')) i += 1
  return i
}

function definir(objeto: Record<string, unknown>, chave: string, valor: unknown) {
  // `defineProperty` evita que a chave "__proto__" mude o protótipo do objeto.
  Object.defineProperty(objeto, chave, {
    value: valor,
    enumerable: true,
    writable: true,
    configurable: true,
  })
}

function lerObjeto(texto: string, inicio: number): [Record<string, unknown>, number] {
  const ultimo = texto.length - 1
  const objeto: Record<string, unknown> = {}
  let i = pularEspacos(texto, inicio)
  if (i > ultimo || texto[i] !== '}') {
    for (;;) {
      if (i > ultimo || texto[i] !== '"') {
        throw new FalhaDeLeitura('Expecting property name enclosed in double quotes', i)
      }
      const [chave, depoisChave] = lerTexto(texto, i + 1)
      i = pularEspacos(texto, depoisChave)
      if (i > ultimo || texto[i] !== ':') throw new FalhaDeLeitura("Expecting ':' delimiter", i)
      i = pularEspacos(texto, i + 1)
      const [valor, depoisValor] = lerValor(texto, i)
      definir(objeto, chave, valor)
      i = pularEspacos(texto, depoisValor)
      if (i <= ultimo && texto[i] === '}') break
      if (i > ultimo || texto[i] !== ',') throw new FalhaDeLeitura("Expecting ',' delimiter", i)
      i = pularEspacos(texto, i + 1)
    }
  }
  return [objeto, i + 1]
}

function lerLista(texto: string, inicio: number): [unknown[], number] {
  const ultimo = texto.length - 1
  const lista: unknown[] = []
  let i = pularEspacos(texto, inicio)
  if (i > ultimo || texto[i] !== ']') {
    for (;;) {
      const [valor, depois] = lerValor(texto, i)
      lista.push(valor)
      i = pularEspacos(texto, depois)
      if (i <= ultimo && texto[i] === ']') break
      if (i > ultimo || texto[i] !== ',') throw new FalhaDeLeitura("Expecting ',' delimiter", i)
      i = pularEspacos(texto, i + 1)
    }
  }
  return [lista, i + 1]
}

function lerValor(texto: string, i: number): [unknown, number] {
  if (i >= texto.length) throw new ParadaDeLeitura(i)
  const c = texto[i]
  const comeca = (palavra: string) => texto.startsWith(palavra, i)
  switch (c) {
    case '"':
      return lerTexto(texto, i + 1)
    case '{':
      return lerObjeto(texto, i + 1)
    case '[':
      return lerLista(texto, i + 1)
    case 'n':
      if (comeca('null')) return [null, i + 4]
      break
    case 't':
      if (comeca('true')) return [true, i + 4]
      break
    case 'f':
      if (comeca('false')) return [false, i + 5]
      break
    case 'N':
      if (comeca('NaN')) return [new NumeroPython(NaN, true, 'NaN'), i + 3]
      break
    case 'I':
      if (comeca('Infinity')) return [new NumeroPython(Infinity, true, 'Infinity'), i + 8]
      break
    case '-':
      if (comeca('-Infinity')) return [new NumeroPython(-Infinity, true, '-Infinity'), i + 9]
      break
  }
  return lerNumero(texto, i)
}

/** `json.loads` do Python: devolve o valor ou lança `ErroJsonPython` com a mesma mensagem. */
export function lerJsonPython(texto: string): unknown {
  try {
    const inicio = pularEspacos(texto, 0)
    const [valor, depois] = lerValor(texto, inicio)
    const fim = pularEspacos(texto, depois)
    if (fim !== texto.length) throw new FalhaDeLeitura('Extra data', fim)
    return valor
  } catch (e) {
    if (e instanceof ParadaDeLeitura) {
      throw new ErroJsonPython(mensagemPython(texto, 'Expecting value', e.posicao))
    }
    if (e instanceof FalhaDeLeitura) {
      throw new ErroJsonPython(mensagemPython(texto, e.mensagem, e.posicao))
    }
    throw e
  }
}
